/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/es/button/Button.tsx';
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React, { Children } from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import { ConfigContext } from '../config-provider';
import AnimationContext from '../config-provider/AnimationContext';
import { tuple } from '../_util/type';
import warning from 'antd/es/_util/warning';
import { cloneElement } from 'antd/es/_util/reactNode';
import SizeContext from '../config-provider/SizeContext';
import Group from './ButtonGroup';
import Loading from '../loading';
// B-Design adds the type. 'ghost' or 'dahsed' is not provided with styles.
const ButtonTypes = tuple('primary', 'secondary', 'default', 'text', 'link', 'ghost', 'dahsed');
const ButtonShapes = tuple('default', 'circle', 'round');
const ButtonHTMLTypes = tuple('submit', 'button', 'reset');
export function convertLegacyProps(type) {
    if (type === 'danger') {
        return { danger: true };
    }
    return { type };
}
const rxTwoCNChar = /^[\u4e00-\u9fa5]{2}$/;
const isTwoCNChar = rxTwoCNChar.test.bind(rxTwoCNChar);
function isString(str) {
    return typeof str === 'string';
}
function isUnborderedButtonType(type) {
    return type === 'text' || type === 'link';
}
function isReactFragment(node) {
    return React.isValidElement(node) && node.type === React.Fragment;
}
// Insert one space between two chinese characters automatically.
function insertSpace(child, needInserted) {
    // Check the child if is undefined or null.
    if (child == null) {
        return;
    }
    const SPACE = needInserted ? ' ' : '';
    // strictNullChecks oops.
    if (typeof child !== 'string' &&
        typeof child !== 'number' &&
        isString(child.type) &&
        isTwoCNChar(child.props.children)) {
        return cloneElement(child, {
            children: child.props.children.split('').join(SPACE)
        });
    }
    if (typeof child === 'string') {
        return isTwoCNChar(child) ? React.createElement("span", null, child.split('').join(SPACE)) : React.createElement("span", null, child);
    }
    if (isReactFragment(child)) {
        return React.createElement("span", null, child);
    }
    return child;
}
function spaceChildren(children, needInserted) {
    let isPrevChildPure = false;
    const childList = [];
    React.Children.forEach(children, (child) => {
        const type = typeof child;
        const isCurrentChildPure = type === 'string' || type === 'number';
        if (isPrevChildPure && isCurrentChildPure) {
            const lastIndex = childList.length - 1;
            const lastChild = childList[lastIndex];
            childList[lastIndex] = `${lastChild}${child}`;
        }
        else {
            childList.push(child);
        }
        isPrevChildPure = isCurrentChildPure;
    });
    // Pass to React.Children.map to auto fill key
    return React.Children.map(childList, (child) => insertSpace(child, needInserted));
}
const InternalButton = (props, ref) => {
    const { loading = false, prefixCls: customizePrefixCls, type = 'default', danger, shape = 'default', size: customizeSize, className, children, icon, underline, ghost = false, block = false, 
    /** If we extract items here, we don't need use omit.js */
    // React does not recognize the `htmlType` prop on a DOM element. Here we pick it out of `rest`.
    htmlType = 'button', ...rest } = props;
    const size = React.useContext(SizeContext);
    const [innerLoading, setLoading] = React.useState(!!loading);
    const [hasTwoCNChar, setHasTwoCNChar] = React.useState(false);
    const { getPrefixCls, autoInsertSpaceInButton, direction } = React.useContext(ConfigContext);
    const animation = React.useContext(AnimationContext);
    const buttonRef = ref || React.createRef();
    const delayTimeoutRef = React.useRef();
    const isNeedInserted = () => React.Children.count(children) === 1 && !icon && !isUnborderedButtonType(type);
    const fixTwoCNChar = () => {
        // Fix for HOC usage like <FormatMessage />
        if (!buttonRef || !buttonRef.current || autoInsertSpaceInButton === false) {
            return;
        }
        const buttonText = buttonRef.current.textContent;
        if (isNeedInserted() && isTwoCNChar(buttonText)) {
            if (!hasTwoCNChar) {
                setHasTwoCNChar(true);
            }
        }
        else if (hasTwoCNChar) {
            setHasTwoCNChar(false);
        }
    };
    // =============== Update Loading ===============
    let loadingOrDelay;
    if (typeof loading === 'object' && loading.delay) {
        loadingOrDelay = loading.delay || true;
    }
    else {
        loadingOrDelay = !!loading;
    }
    React.useEffect(() => {
        clearTimeout(delayTimeoutRef.current);
        if (typeof loadingOrDelay === 'number') {
            delayTimeoutRef.current = window.setTimeout(() => {
                setLoading(loadingOrDelay);
            }, loadingOrDelay);
        }
        else {
            setLoading(loadingOrDelay);
        }
    }, [loadingOrDelay]);
    React.useEffect(fixTwoCNChar, [buttonRef]);
    const handleClick = (e) => {
        const { onClick, disabled } = props;
        // https://github.com/ant-design/ant-design/issues/30207
        if (innerLoading || disabled) {
            e.preventDefault();
            return;
        }
        onClick?.(e);
    };
    if (process.env.NODE_ENV !== 'production')
        warning(!(type === 'dahsed'), 'Button', `there is no style corressponding to  \`dashed\` type in B-Design 3.0`);
    warning(!(typeof icon === 'string' && icon.length > 2), 'Button', `\`icon\` is using ReactNode instead of string naming in v4. Please check \`${icon}\` at https://ant.design/components/icon`);
    warning(!(ghost && isUnborderedButtonType(type)), 'Button', "`link` or `text` button can't be a `ghost` button.");
    const prefixCls = getPrefixCls('btn', customizePrefixCls);
    const autoInsertSpace = autoInsertSpaceInButton !== false;
    // large => lg
    // small => sm
    // xs => xs
    let sizeCls = '';
    switch (customizeSize || size) {
        case 'large':
            sizeCls = 'lg';
            break;
        case 'small':
            sizeCls = 'sm';
            break;
        case 'xs':
            sizeCls = 'xs';
            break;
        case 'xl':
            sizeCls = 'xl';
            break;
        default:
            break;
    }
    const iconType = innerLoading ? 'loading' : icon;
    const isIconBefore = () => {
        const numOfChildren = Children.count(children);
        // If there is icon in the props with children or loading is true and the child is not empy string.
        if ((icon || loading) && numOfChildren > 0 && !!children) {
            return true;
        }
        else if (numOfChildren > 1 && children[0]?.type?.name === 'Icon') {
            // If the type of the first child is icon when tehre is more than 2 children
            return true;
        }
        else {
            return false;
        }
    };
    const isIconAfter = () => {
        if (Children.count(children) > 1) {
            if (children[Children.count(children) - 1].type?.name === 'Icon') {
                return true;
            }
        }
        else {
            return false;
        }
    };
    const classes = classNames(prefixCls, {
        // @ts-ignore
        [`${prefixCls}-${type}`]: type && type !== 'dashed',
        [`${prefixCls}-breathing`]: !!animation?.breathing && (type === 'secondary' || type === 'primary' || type === 'ghost'),
        [`${prefixCls}-${shape}`]: shape !== 'default' && shape,
        [`${prefixCls}-${sizeCls}`]: sizeCls,
        [`${prefixCls}-icon-only`]: !children && children !== 0 && !!iconType,
        [`${prefixCls}-loading`]: innerLoading,
        [`${prefixCls}-two-chinese-chars`]: hasTwoCNChar && autoInsertSpace,
        [`${prefixCls}-block`]: block,
        [`${prefixCls}-dangerous`]: !!danger,
        [`${prefixCls}-rtl`]: direction === 'rtl',
        [`${prefixCls}-underline`]: underline && type === 'link',
        [`${prefixCls}-icon-before`]: isIconBefore(),
        [`${prefixCls}-icon-after`]: isIconAfter()
    }, className);
    const iconNode = icon && !innerLoading ? (icon) : innerLoading ? (React.createElement(Loading, { color: 'white', size: sizeCls === 'xs' || sizeCls === 'sm' ? 'small' : 'default' })) : null;
    const kids = children || children === 0 ? spaceChildren(children, isNeedInserted() && autoInsertSpace) : null;
    const linkButtonRestProps = omit(rest, ['navigate']);
    if (linkButtonRestProps.href !== undefined) {
        return (React.createElement("a", { ...linkButtonRestProps, className: classes, onClick: handleClick, ref: buttonRef },
            iconNode,
            kids));
    }
    const buttonNode = (React.createElement("button", { ...rest, type: htmlType, className: classes, onClick: handleClick, ref: buttonRef },
        iconNode,
        kids));
    // B-Design removes the embedded Wave component inside Button component.
    return buttonNode;
};
const Button = React.forwardRef(InternalButton);
Button.displayName = 'Button';
Button.Group = Group;
Button.__ANT_BUTTON = true;
// export default withConfigProvider(Button);
export default Button;
